/**
 * useRealtimeScoring — Live scoring events via Supabase Realtime
 *
 * Subscribes to INSERT events on scoring_results table.
 * Returns a rolling buffer of recent decisions for live dashboard display.
 */

import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface LiveScoringEvent {
  id: string;
  session_id: string;
  intent_score: number;
  monetization_score: number;
  decision: string;
  vertical_id: string | null;
  computed_at: string;
}

const MAX_BUFFER = 50;

export function useRealtimeScoring(enabled = true) {
  const [events, setEvents] = useState<LiveScoringEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const channel = supabase
      .channel('scoring-results-live')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'scoring_results',
        },
        (payload) => {
          const row = payload.new as LiveScoringEvent;
          setEvents((prev) => {
            const next = [row, ...prev];
            return next.slice(0, MAX_BUFFER);
          });
        },
      )
      .subscribe((status) => {
        setConnected(status === 'SUBSCRIBED');
      });

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [enabled]);

  return { events, connected };
}

/**
 * useRealtimeOutcomes — Live outcome events
 */
export function useRealtimeOutcomes(enabled = true) {
  const [outcomes, setOutcomes] = useState<any[]>([]);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const channel = supabase
      .channel('outcomes-live')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'outcomes',
        },
        (payload) => {
          setOutcomes((prev) => {
            const next = [payload.new, ...prev];
            return next.slice(0, MAX_BUFFER);
          });
        },
      )
      .subscribe((status) => {
        setConnected(status === 'SUBSCRIBED');
      });

    return () => {
      channel.unsubscribe();
    };
  }, [enabled]);

  return { outcomes, connected };
}
